import { useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { ImagePlus, X, Loader2, Video, Play } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import {
  reportContent,
  ReportPayload,
  ReportTargetType as ApiReportTargetType,
} from "@/api/community";
import { uploadFileToServer } from "@/utils/upload";

export type ReportTargetType = "post" | "comment" | "reply" | "event";

// Map UI target types to API target types
const mapToApiTargetType = (targetType: ReportTargetType): ApiReportTargetType => {
  switch (targetType) {
    case "reply":
      return "comment";
    case "event":
      return "post";
    default:
      return targetType;
  }
};

export interface ReportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  targetId: string;
  targetType: ReportTargetType;
  onReportSubmitted?: () => void;
}

interface MediaFile {
  file: File;
  preview: string;
  type: "image" | "video";
}

const REPORT_CATEGORIES = [
  {
    id: "spam",
    labelKey: "community.report.categories.spam",
    descKey: "community.report.categories.spam_desc",
  },
  {
    id: "harassment",
    labelKey: "community.report.categories.harassment",
    descKey: "community.report.categories.harassment_desc",
  },
  {
    id: "inappropriate",
    labelKey: "community.report.categories.inappropriate",
    descKey: "community.report.categories.inappropriate_desc",
  },
  {
    id: "hate_speech",
    labelKey: "community.report.categories.hate_speech",
    descKey: "community.report.categories.hate_speech_desc",
  },
  {
    id: "other",
    labelKey: "community.report.categories.other",
    descKey: "community.report.categories.other_desc",
  },
] as const;

const MAX_ATTACHMENTS = 5;
const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB for videos
const MAX_IMAGE_SIZE = 10 * 1024 * 1024; // 10MB for images

const VALID_IMAGE_TYPES = ["image/jpeg", "image/png", "image/gif", "image/webp", "image/bmp"];
const VALID_VIDEO_TYPES = ["video/mp4", "video/webm", "video/quicktime", "video/x-msvideo"];

export function ReportDialog({
  open,
  onOpenChange,
  targetId,
  targetType,
  onReportSubmitted,
}: ReportDialogProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [selectedCategory, setSelectedCategory] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [mediaFiles, setMediaFiles] = useState<MediaFile[]>([]);
  const [isUploadingMedia, setIsUploadingMedia] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [viewingMedia, setViewingMedia] = useState<MediaFile | null>(null);
  const [showDiscardConfirmation, setShowDiscardConfirmation] = useState(false);
  const [mediaIndexToRemove, setMediaIndexToRemove] = useState<number | null>(null);

  const isBusy = isUploadingMedia || isSubmitting;

  const hasUnsavedChanges = () => {
    return selectedCategory !== "" || description.trim() !== "" || mediaFiles.length > 0;
  };

  const resetForm = () => {
    setSelectedCategory("");
    setDescription("");
    // Revoke object URLs to prevent memory leaks
    mediaFiles.forEach((media) => {
      if (media.preview.startsWith("blob:")) {
        URL.revokeObjectURL(media.preview);
      }
    });
    setMediaFiles([]);
  };

  const handleOpenChange = (newOpen: boolean) => {
    // Prevent closing dialog while submitting
    if (!newOpen && isBusy) {
      return;
    }
    if (!newOpen && hasUnsavedChanges()) {
      setShowDiscardConfirmation(true);
      return;
    }
    onOpenChange(newOpen);
    if (!newOpen) {
      resetForm();
    }
  };

  const handleConfirmDiscard = () => {
    setShowDiscardConfirmation(false);
    resetForm();
    onOpenChange(false);
  };

  const handleCancelDiscard = () => {
    setShowDiscardConfirmation(false);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const remainingSlots = MAX_ATTACHMENTS - mediaFiles.length;
    if (remainingSlots <= 0) {
      toast({
        title: t("community.report.max_attachments_reached"),
        description: t("community.report.max_attachments_reached_desc", { max: MAX_ATTACHMENTS }),
        variant: "destructive",
      });
      return;
    }

    const filesToAdd = Array.from(files).slice(0, remainingSlots);
    const newMediaFiles: MediaFile[] = [];

    for (const file of filesToAdd) {
      const isImage = VALID_IMAGE_TYPES.includes(file.type);
      const isVideo = VALID_VIDEO_TYPES.includes(file.type);

      if (!isImage && !isVideo) {
        toast({
          title: t("community.report.invalid_file_type"),
          description: t("community.report.invalid_file_type_desc"),
          variant: "destructive",
        });
        continue;
      }

      const maxSize = isImage ? MAX_IMAGE_SIZE : MAX_FILE_SIZE;
      if (file.size > maxSize) {
        toast({
          title: t("community.report.file_too_large"),
          description: isImage
            ? t("community.report.image_too_large_desc")
            : t("community.report.video_too_large_desc"),
          variant: "destructive",
        });
        continue;
      }

      const preview = URL.createObjectURL(file);
      newMediaFiles.push({
        file,
        preview,
        type: isImage ? "image" : "video",
      });
    }

    if (newMediaFiles.length > 0) {
      setMediaFiles((prev) => [...prev, ...newMediaFiles]);
    }

    // Reset input to allow selecting the same file again
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const removeMedia = (index: number) => {
    setMediaFiles((prev) => {
      const media = prev[index];
      if (media.preview.startsWith("blob:")) {
        URL.revokeObjectURL(media.preview);
      }
      return prev.filter((_, i) => i !== index);
    });
  };

  const closeAndReset = () => {
    setShowDiscardConfirmation(false);
    resetForm();
    onOpenChange(false);
  };

  const handleSubmit = async () => {
    if (!selectedCategory) {
      toast({
        title: t("community.report.select_category"),
        description: t("community.report.select_category_desc"),
        variant: "destructive",
      });
      return;
    }

    // Require description when "other" is selected
    if (selectedCategory === "other" && !description.trim()) {
      toast({
        title: t("community.report.details_required"),
        description: t("community.report.details_required_desc"),
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const mediaUrls: string[] = [];

      setIsUploadingMedia(true);
      // Upload all media files
      for (const media of mediaFiles) {
        try {
          const uploadResult = await uploadFileToServer({ file: media.file });
          if (uploadResult.url) {
            mediaUrls.push(uploadResult.url);
          }
        } catch {
          toast({
            title: t("community.report.upload_failed"),
            description: t("community.report.upload_failed_desc"),
            variant: "destructive",
          });
          setIsUploadingMedia(false);
          setIsSubmitting(false);
          return;
        }
      }

      setIsSubmitting(true);
      const payload: ReportPayload = {
        reason: selectedCategory,
        description: description.trim() || undefined,
        media_urls: mediaUrls.length > 0 ? mediaUrls : undefined,
      };

      await reportContent(mapToApiTargetType(targetType), targetId, payload);

      toast({
        title: t("community.report.submitted"),
        description: t("community.report.submitted_desc"),
      });

      onReportSubmitted?.();
      closeAndReset();
    } catch (error: unknown) {
      setIsUploadingMedia(false);
      setIsSubmitting(false);

      const axiosError = error as { response?: { data?: { error?: { message?: string } } } };
      const errorMessage = axiosError?.response?.data?.error?.message;
      toast({
        title: t("community.report.failed"),
        description: errorMessage || t("community.report.failed_desc"),
        variant: "destructive",
      });
      return;
    } finally {
      setIsSubmitting(false);
      setIsUploadingMedia(false);
    }
  };

  const getDialogTitle = () => {
    switch (targetType) {
      case "post":
        return t("community.report.title_post");
      case "comment":
        return t("community.report.title_comment");
      case "reply":
        return t("community.report.title_reply");
      case "event":
        return t("community.report.title_event");
      default:
        return t("community.report.title");
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="flex max-h-[90vh] max-w-xl flex-col"
        closeButtonDisabled={isBusy}
        onEscapeKeyDown={(e) => {
          if (isBusy) e.preventDefault();
        }}
        onPointerDownOutside={(e) => {
          if (isBusy) e.preventDefault();
        }}
        onInteractOutside={(e) => {
          if (isBusy) e.preventDefault();
        }}
      >
        <DialogHeader className="shrink-0">
          <DialogTitle className="text-lg font-semibold tracking-tight">
            {getDialogTitle()}
          </DialogTitle>
          <DialogDescription>{t("community.report.category_label")}</DialogDescription>
        </DialogHeader>

        <div className="flex-1 space-y-4 overflow-y-auto px-2 py-4">
          {/* Category Selection - Scrollable */}
          <div className="space-y-3">
            <RadioGroup
              value={selectedCategory}
              onValueChange={setSelectedCategory}
              className="space-y-1"
              disabled={isBusy}
            >
              {REPORT_CATEGORIES.map((category) => (
                <div
                  key={category.id}
                  className={`flex items-start space-x-3 rounded-lg border p-3 transition-colors ${
                    isSubmitting ? "cursor-not-allowed opacity-60" : "cursor-pointer"
                  } ${
                    selectedCategory === category.id
                      ? "border-primary bg-primary/5"
                      : `border-border ${!isSubmitting ? "hover:bg-muted/50" : ""}`
                  }`}
                  onClick={() => !isSubmitting && setSelectedCategory(category.id)}
                >
                  <RadioGroupItem value={category.id} id={category.id} className="mt-1.5" />
                  <div className="flex-1 space-y-1">
                    <Label htmlFor={category.id} className="cursor-pointer text-sm font-medium">
                      {t(category.labelKey)}
                    </Label>
                    <p className="text-muted-foreground text-xs leading-relaxed">
                      {t(category.descKey)}
                    </p>
                  </div>
                </div>
              ))}
            </RadioGroup>
          </div>

          {/* Description textarea for "Something else" */}
          {selectedCategory === "other" && (
            <div className="space-y-2">
              <Label htmlFor="description">
                {t("community.report.details_label_required")}
                <span className="text-destructive ml-1">*</span>
              </Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={t("community.report.details_placeholder")}
                className="min-h-[100px] resize-none"
                disabled={isBusy}
              />
            </div>
          )}

          {/* Media Attachments */}
          <div className="space-y-2">
            <Label className="block pb-1">{t("community.report.attachments_label")}</Label>

            {/* Media Previews */}
            {mediaFiles.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {mediaFiles.map((media, index) => (
                  <div key={index} className="group relative">
                    {media.type === "image" ? (
                      <div
                        className={`relative h-20 w-20 overflow-hidden rounded-md border ${
                          isSubmitting ? "cursor-not-allowed opacity-60" : "cursor-pointer"
                        }`}
                        onClick={() => !isSubmitting && setViewingMedia(media)}
                      >
                        <img
                          src={media.preview}
                          alt={`Attachment ${index + 1}`}
                          className="h-full w-full object-cover transition-opacity group-hover:opacity-80"
                        />
                      </div>
                    ) : (
                      <div
                        className={`bg-muted relative flex h-20 w-20 items-center justify-center overflow-hidden rounded-md border ${
                          isSubmitting ? "cursor-not-allowed opacity-60" : "cursor-pointer"
                        }`}
                        onClick={() => !isSubmitting && setViewingMedia(media)}
                      >
                        <video src={media.preview} className="h-full w-full object-cover" />
                        <div className="absolute inset-0 flex items-center justify-center bg-black/30 transition-opacity group-hover:bg-black/50">
                          <Play className="h-6 w-6 text-white" />
                        </div>
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!isSubmitting) {
                          setMediaIndexToRemove(index);
                        }
                      }}
                      disabled={isBusy}
                      className={`bg-destructive text-destructive-foreground absolute -top-2 -right-2 z-10 rounded-full p-1 ${
                        isSubmitting ? "cursor-not-allowed opacity-50" : "hover:bg-destructive/90"
                      }`}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Add Attachment Button */}
            {mediaFiles.length < MAX_ATTACHMENTS && (
              <div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*,video/*"
                  onChange={handleFileSelect}
                  className="hidden"
                  id="media-upload"
                  multiple
                  disabled={isBusy}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  className="gap-2"
                  disabled={isBusy}
                >
                  <ImagePlus className="h-4 w-4" />
                  <Video className="h-4 w-4" />
                  {t("community.report.attach_media")}
                </Button>
                <p className="text-muted-foreground mt-1 text-xs">
                  {t("community.report.attachments_hint", {
                    current: mediaFiles.length,
                    max: MAX_ATTACHMENTS,
                  })}
                </p>
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="shrink-0 gap-2 border-t pt-4 sm:gap-0">
          <Button
            type="button"
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={isBusy}
          >
            {t("community.report.cancel")}
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={
              isSubmitting ||
              !selectedCategory ||
              (selectedCategory === "other" && !description.trim())
            }
          >
            {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t("community.report.submit")}
          </Button>
        </DialogFooter>
      </DialogContent>

      {/* Media View Modal */}
      <Dialog open={!!viewingMedia} onOpenChange={(open) => !open && setViewingMedia(null)}>
        <DialogContent className="max-h-[90vh] max-w-4xl overflow-hidden p-0">
          <DialogHeader className="sr-only">
            <DialogTitle>{t("community.report.view_attachment")}</DialogTitle>
          </DialogHeader>
          <button
            type="button"
            onClick={() => setViewingMedia(null)}
            className="absolute top-2 right-2 z-50 rounded-full bg-black/50 p-2 text-white transition-colors hover:bg-black/70"
          >
            <X className="h-5 w-5" />
          </button>
          {viewingMedia?.type === "image" ? (
            <div className="flex min-h-[300px] items-center justify-center bg-black/95">
              <img
                src={viewingMedia.preview}
                alt={t("community.report.attachment_alt")}
                className="max-h-[85vh] max-w-full object-contain"
              />
            </div>
          ) : viewingMedia?.type === "video" ? (
            <div className="flex min-h-[300px] items-center justify-center bg-black">
              <video
                src={viewingMedia.preview}
                controls
                autoPlay
                className="max-h-[85vh] max-w-full"
              />
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Discard Confirmation Dialog - Don't show when submitting */}
      <AlertDialog
        open={showDiscardConfirmation && !isBusy}
        onOpenChange={(open) => !isBusy && setShowDiscardConfirmation(open)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("community.report.discard_title")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("community.report.discard_description")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCancelDiscard} disabled={isBusy}>
              {t("community.report.discard_no")}
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmDiscard} disabled={isBusy}>
              {t("community.report.discard_yes")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Remove Attachment Confirmation Dialog */}
      <AlertDialog
        open={mediaIndexToRemove !== null}
        onOpenChange={(open) => {
          if (!open) setMediaIndexToRemove(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("community.report.remove_attachment_title")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("community.report.remove_attachment_description")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setMediaIndexToRemove(null)}>
              {t("community.report.remove_attachment_cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (mediaIndexToRemove !== null) {
                  removeMedia(mediaIndexToRemove);
                  setMediaIndexToRemove(null);
                }
              }}
            >
              {t("community.report.remove_attachment_confirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
}
